import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { ITodolistFlow, ITodolistTaskGroup } from '~/types/todolist'

import Button from '~/components/Buttons/Button'
import MultiSelectInput from '~/components/Inputs/ReactHookForm/MultiSelectInput'
import SelectInput from '~/components/Inputs/ReactHookForm/SelectInput'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import RequiredInformation from '~/components/RequiredInformation'
import { FORM_ERRORS } from '~/const'
import { useTodolistTemplatesQuery } from '~/queries/todolist-templates'
import { keys as todolistKeys, useCreateTodolistMutation, useUpdateTodolistMutation } from '~/queries/todolists'
import { useUsersListQuery } from '~/queries/users'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { capitalize } from '~/utils/string'
import { displayToast } from '~/utils/toast'

const MODAL_ID = 'set-todolist-modal'

interface FormValues {
  groups: ITodolistTaskGroup[]
  template_id: string
  title: string
}

const validationSchema = Yup.object({
  groups: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().trim().required(),
        user_ids: Yup.array().of(Yup.string().required()).required(),
      }).required(FORM_ERRORS.REQUIRED)
    )
    .required(),
  template_id: Yup.string().required(FORM_ERRORS.REQUIRED),
  title: Yup.string().required(FORM_ERRORS.REQUIRED),
})

interface FormProps {
  appKey: string
  deviceType: string
  onClose: VoidFunction
  onFailure: VoidFunction
  onSuccess: VoidFunction
  todolistFlow?: ITodolistFlow
}

const Form = ({ appKey, deviceType, onClose, onFailure, onSuccess, todolistFlow }: FormProps): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      groups:
        todolistFlow?.tasks.reduce((acc, { groups }) => {
          groups.forEach((group) => {
            if (!acc.find(({ name }) => name === group.name)) {
              acc.push(group)
            }
          })

          return acc
        }, [] as ITodolistTaskGroup[]) || [],
      template_id: todolistFlow?.todolist.template_id || '',
      title: todolistFlow?.todolist.title || '',
    },
    resolver: yupResolver(validationSchema),
  })

  const { append, fields, update } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormProvider)
    name: 'groups', // unique name for your Field Array
  })

  const selectedTemplate = watch('template_id')

  const groups = watch('groups')

  /** Queries & Mutations */

  const { mutateAsync: createTodolist } = useCreateTodolistMutation()

  const { mutateAsync: updateTodolist } = useUpdateTodolistMutation()

  const { data: todolistTemplates } = useTodolistTemplatesQuery()

  const alphabeticSorting = ({ label: a }: IOption, { label: b }: IOption) => a.localeCompare(b)

  const todolistTemplatesOptions =
    todolistTemplates
      ?.map(({ template }) => ({
        label: template.title,
        value: template.template_id,
      }))
      .sort(alphabeticSorting) || []

  const { data: users, isLoading: isUsersLoading } = useUsersListQuery()

  const userOptions =
    users
      ?.filter(({ email }) => email.length > 0)
      .map(({ name, picture, profile, user_id }) => ({
        label: capitalize(name),
        userData: {
          job: profile?.job_title,
          name: capitalize(name),
          picture: picture,
        },
        value: user_id,
      }))
      .sort(alphabeticSorting) || []

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    try {
      if (todolistFlow) {
        await updateTodolist({
          groups: values.groups,
          title: values.title,
          todolistId: todolistFlow.todolist.id,
        })
        await queryClient.invalidateQueries({ queryKey: todolistKeys.details(todolistFlow.todolist.id) })
      } else {
        await createTodolist({ ...values, app_key: appKey, device_type: deviceType })
      }
      await queryClient.invalidateQueries({ queryKey: todolistKeys.list({ appKey, deviceType }) })
      displayToast(`To-do successfully ${todolistFlow ? 'updated' : 'created'}`, 'success')
      onSuccess()
    } catch (e) {
      captureException(e)
      displayToast(`To-do could not be ${todolistFlow ? 'updated' : 'created'}!`, 'error')
      onFailure()
    }
  }

  const handleChangeTemplate = async (option: IOption) => {
    const _todolistTemplate = todolistTemplates?.find(({ template }) => template.template_id === option.value)

    if (_todolistTemplate) {
      const groups = [...new Set(_todolistTemplate.tasks.map(({ group_names }) => group_names).flat())]
      const title = _todolistTemplate.template.title

      setValue('template_id', option.value)
      setValue('title', title)

      for (const groupName of groups) {
        append({ name: groupName, user_ids: [] })
      }
    }
  }

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody className="set-todolist-modal__body">
        <RequiredInformation />
        <SelectInput
          {...register('template_id')}
          {...reactSelectMenuPositionDef(MODAL_ID)}
          error={errors.template_id?.message}
          label="Template"
          menuClassName="todolist-template-select-input-menu"
          onChange={(value) => handleChangeTemplate(value as IOption)}
          options={todolistTemplatesOptions}
          placeholder="Select a template"
          required
          value={todolistTemplatesOptions.find(({ value }) => value === selectedTemplate)}
        />
        {selectedTemplate ? (
          <>
            <TextInput
              {...register('title')}
              error={errors.title?.message}
              label="Title"
              placeholder="Enter title"
              required
            />
            {fields.map((field, index) => (
              <div key={field.id}>
                <Controller
                  control={control}
                  name={`groups.${index}.user_ids`}
                  render={({ field }) => (
                    <MultiSelectInput
                      {...field}
                      {...reactSelectMenuPositionDef(MODAL_ID)}
                      error={errors.groups?.[index]?.name?.message}
                      isLoading={isUsersLoading}
                      label={`Choose users for the group ${groups[index]?.name}`}
                      onChange={(options) => {
                        update(index, {
                          name: groups[index]?.name,
                          user_ids: (options as IOption[]).map(({ value }) => value),
                        })
                      }}
                      options={userOptions}
                      placeholder="Select assignees"
                      required
                      value={userOptions.filter(({ value }) => groups[index]?.user_ids.includes(value))}
                    />
                  )}
                />
              </div>
            ))}
          </>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          Save
        </Button>
      </ModalFooter>
      <DevTool control={control} />
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const SetTodolistModal = ({
  appKey,
  deviceType,
  isOpen,
  onClose,
  onFailure,
  onSuccess,
  todolistFlow,
}: ModalProps): ReactElement => {
  return (
    <Modal className="set-todolist-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>{!!todolistFlow ? 'Edit to-do list' : 'Create a to-do list'}</ModalHeader>
      <Form
        appKey={appKey}
        deviceType={deviceType}
        onClose={onClose}
        onFailure={onFailure}
        onSuccess={onSuccess}
        todolistFlow={todolistFlow}
      />
    </Modal>
  )
}

export default SetTodolistModal
